import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  Injector,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha-2';

import { environment } from '../environments/environment';

import { buildRouterConfiguration } from './app.routes';
import { INTERCEPTORS } from './core/interceptors/interceptors';
import { Logger } from './core/observability/logger';
import {
  LoggerFactory,
  MDB_LOGGER_FACTORY,
  provideObservability,
} from './core/observability/provider';
import { BrowserService, MDB_WINDOW } from './services/browser/browser.service';
import { DirectLinkDetailsService } from './services/direct-link/direct-link-details/direct-link-details.service';
import {
  provideAppBaseHref,
  VirtualSiteService,
} from './services/virtual-site/virtual-site.service';

const STATIC_PROVIDERS = [
  provideHttpClient(withInterceptors(INTERCEPTORS)),
  buildRouterConfiguration(),
  {
    provide: RECAPTCHA_SETTINGS,
    useValue: {
      siteKey: environment.recaptcha_site_key,
    } as RecaptchaSettings,
  },
];

async function buildConfiguration(): Promise<ApplicationConfig> {
  const loggerFactory: LoggerFactory = (name: string) => new Logger(name);

  // provideAppBaseHref runs before Angular is bootstrapped, so there is no
  // injection context yet. BrowserService now relies on inject(), so we spin up
  // a minimal injector to instantiate the pre-bootstrap dependencies.
  const preBootstrapInjector = Injector.create({
    providers: [
      { provide: MDB_LOGGER_FACTORY, useValue: loggerFactory },
      { provide: MDB_WINDOW, useValue: window },
      { provide: BrowserService },
      { provide: DirectLinkDetailsService },
      { provide: VirtualSiteService },
    ],
  });

  const virtualSiteService = preBootstrapInjector.get(VirtualSiteService);

  return {
    providers: [
      ...STATIC_PROVIDERS,
      provideObservability(),
      provideZoneChangeDetection(),
      await provideAppBaseHref(virtualSiteService),
      { provide: VirtualSiteService, useValue: virtualSiteService },
      provideNoopAnimations(),
    ],
  } as ApplicationConfig;
}

export const AppConfig = {
  buildConfiguration: buildConfiguration,
};
